/**
 * Gap analysis.
 *
 * LAYOUT, TOP TO BOTTOM
 *   1. Four breakdown donuts — framework, compliance, gap status, plan status.
 *   2. A row of KPI tiles that double as one-click filters.
 *   3. Filter controls.
 *   4. The gaps themselves, grouped by the requirement section they came from.
 *
 * WHY GROUPED RATHER THAN A FLAT LIST
 * A finalised assessment can produce fifty or more gaps. Flat, that is a wall
 * of text nobody reads to the end. Grouped by the section of the standard they
 * came from, the same fifty gaps become "eight sections, three of which are in
 * trouble" — which is the shape of the problem a compliance lead is actually
 * trying to see.
 *
 * Selection state survives filtering on purpose: filter to non-compliant,
 * select them, widen the filter, and your selection is still there. Losing it
 * on every filter change would make bulk plan creation infuriating.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, CardHeader, Button, Pill, Modal, Field, EmptyState, Skeleton,
  ErrorNotice, TONE_FOR_STATUS,
} from '../components/ui';
import { DonutChart, type Datum } from '../components/charts';
import {
  IconGap, IconChevron, IconGrid, IconList,
  IconKey, IconScan, IconIncident, IconVendor, IconUsers, IconChecklist, IconPolicy, IconShield,
} from '../components/icons';
import type { SVGProps, ComponentType } from 'react';
import { useGapReuse, GapReuseNote, type GapReuseEntry } from '../components/EvidenceReuse';
import { cn } from '../lib/cn';

interface Gap {
  id: string;
  title: string;
  description: string | null;
  severity: string;
  status: string;
  framework: { id: string; key: string; name: string };
  plans: Array<{ plan: { id: string; name: string; status: string } }>;
  answerLabel: string | null;
  questionCode: string | null;
  categoryPath: string | null;
  evidenceMissing: boolean;
  section: { code: string; title: string } | null;
  subsection: { code: string; title: string } | null;
}

interface Breakdown {
  total: number; open: number; closed: number;
  notInPlan: number; evidenceMissing: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  byFramework: Record<string, number>;
  byPlanMembership: Record<string, number>;
}

export function GapsPage() {
  const { t } = useTranslation();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [severity, setSeverity] = useState('');
  const [membership, setMembership] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [planOpen, setPlanOpen] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planDue, setPlanDue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Tiles by default — the visual grid the product owner chose — with the
  // grouped list a click away for anyone who wants gaps bucketed by section.
  // The choice is remembered per browser; a missing or blocked store is fine.
  const [view, setView] = useState<'tiles' | 'grouped'>(() => {
    try {
      return localStorage.getItem('oc-gaps-view') === 'grouped' ? 'grouped' : 'tiles';
    } catch {
      return 'tiles';
    }
  });
  const chooseView = (next: 'tiles' | 'grouped') => {
    setView(next);
    try { localStorage.setItem('oc-gaps-view', next); } catch { /* ignore */ }
  };

  const { data, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['gaps', { search, status, severity, membership }],
    queryFn: () =>
      api.get<{ items: Gap[]; total: number }>(
        `/gaps${qs({ search, status, severity, planMembership: membership, pageSize: 200 })}`,
      ),
  });

  const { data: breakdown } = useQuery({
    queryKey: ['gaps', 'breakdown'],
    queryFn: () => api.get<Breakdown>('/gaps/breakdown'),
  });

  /**
   * Which of these gaps rest on a control this organisation has already proved.
   *
   * One request for the whole list, indexed by gap id, rather than a query per
   * card. Deliberately a SEPARATE request from `/gaps`: gap paging, filtering
   * and counting stay untouched, and if this fails the gap list still renders
   * — the hint disappears, the findings do not.
   */
  const reuseByGap = useGapReuse(allows('gap.read'));

  const createPlan = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/plans', {
        name: planName,
        gapIds: [...selected],
        ...(planDue ? { dueDate: planDue } : {}),
      }),
    onSuccess: () => {
      setPlanOpen(false);
      setSelected(new Set());
      setPlanName('');
      setPlanDue('');
      void queryClient.invalidateQueries();
      navigate('/plans');
    },
    onError: (err) => setError(describeError(err)),
  });

  const items = data?.items ?? [];

  /** Group gaps by their originating requirement section, then subsection. */
  const groups = useMemo(() => {
    const bySection = new Map<
      string,
      { title: string; subgroups: Map<string, { title: string; gaps: Gap[] }> }
    >();

    for (const gap of items) {
      const sectionKey = gap.section?.code ?? '—';
      const sectionTitle = gap.section
        ? `${gap.section.code} ${gap.section.title}`
        : gap.framework.name;

      if (!bySection.has(sectionKey)) {
        bySection.set(sectionKey, { title: sectionTitle, subgroups: new Map() });
      }
      const section = bySection.get(sectionKey)!;

      const subKey = gap.subsection?.code ?? sectionKey;
      const subTitle = gap.subsection
        ? `${gap.subsection.code} ${gap.subsection.title}`
        : sectionTitle;

      if (!section.subgroups.has(subKey)) {
        section.subgroups.set(subKey, { title: subTitle, gaps: [] });
      }
      section.subgroups.get(subKey)!.gaps.push(gap);
    }
    return bySection;
  }, [items]);

  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGroup = (key: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Framework is an IDENTITY, so it takes categorical series colours.
  // Everything else here is a STATUS, so it takes the reserved status ramp.
  const frameworkData: Datum[] = Object.entries(breakdown?.byFramework ?? {}).map(
    ([name, value], i) => ({ key: name, label: name, value, series: i + 1 }),
  );
  const complianceData: Datum[] = [
    { key: 'NON_COMPLIANT', label: t('status.NON_COMPLIANT'), value: breakdown?.bySeverity.NON_COMPLIANT ?? 0, tone: 'critical' },
    { key: 'NEEDS_IMPROVEMENT', label: t('status.NEEDS_IMPROVEMENT'), value: breakdown?.bySeverity.NEEDS_IMPROVEMENT ?? 0, tone: 'warning' },
  ];
  const statusData: Datum[] = [
    { key: 'OPEN', label: t('status.OPEN'), value: breakdown?.byStatus.OPEN ?? 0, tone: 'warning' },
    { key: 'CLOSED', label: t('status.CLOSED'), value: breakdown?.byStatus.CLOSED ?? 0, tone: 'good' },
  ];
  const planData: Datum[] = [
    { key: 'IN_PLAN', label: t('gaps.inPlan'), value: breakdown?.byPlanMembership.IN_PLAN ?? 0, tone: 'good' },
    { key: 'NOT_IN_PLAN', label: t('gaps.notInPlan'), value: breakdown?.byPlanMembership.NOT_IN_PLAN ?? 0, tone: 'serious' },
  ];

  /** A KPI tile that applies a filter when clicked. */
  const kpi = (
    label: string,
    value: number,
    active: boolean,
    onClick: () => void,
    tone?: 'critical' | 'serious' | 'warning',
  ) => (
    <button
      key={label}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'oc-card p-3 text-left transition-all min-h-[4.5rem]',
        active ? 'ring-2 ring-brand border-brand' : 'hover:bg-surface-sunken',
      )}
    >
      <span className={cn(
        'block text-xl font-semibold tabular-nums',
        tone === 'critical' && 'text-critical',
        tone === 'serious' && 'text-serious',
        tone === 'warning' && 'text-warning',
        !tone && 'text-ink',
      )}>{value}</span>
      <span className="block text-xs text-ink-secondary leading-tight mt-0.5">{label}</span>
    </button>
  );

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('gaps.title')}</h1>
        <p className="oc-muted mt-1">{t('gaps.subtitle')}</p>
      </header>

      {error && <ErrorNotice message={error} />}

      {/* Four breakdowns */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader title={t('gaps.breakdownFramework')} />
          <DonutChart data={frameworkData} centerValue={breakdown?.total ?? 0}
                      centerLabel={t('gaps.total')} size={140} />
        </Card>
        <Card>
          <CardHeader title={t('gaps.breakdownSeverity')} />
          <DonutChart data={complianceData} centerValue={breakdown?.total ?? 0}
                      centerLabel={t('gaps.total')} size={140} />
        </Card>
        <Card>
          <CardHeader title={t('gaps.breakdownStatus')} />
          <DonutChart data={statusData} centerValue={breakdown?.total ?? 0}
                      centerLabel={t('gaps.total')} size={140} />
        </Card>
        <Card>
          <CardHeader title={t('gaps.breakdownPlan')} />
          <DonutChart data={planData} centerValue={breakdown?.total ?? 0}
                      centerLabel={t('gaps.total')} size={140} />
        </Card>
      </div>

      {/* KPI tiles as quick filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {kpi(t('gaps.total'), breakdown?.total ?? 0,
             !status && !severity && !membership,
             () => { setStatus(''); setSeverity(''); setMembership(''); })}
        {kpi(t('status.OPEN'), breakdown?.open ?? 0, status === 'OPEN',
             () => { setStatus('OPEN'); setSeverity(''); setMembership(''); }, 'warning')}
        {kpi(t('status.CLOSED'), breakdown?.closed ?? 0, status === 'CLOSED',
             () => { setStatus('CLOSED'); setSeverity(''); setMembership(''); })}
        {kpi(t('status.NEEDS_IMPROVEMENT'), breakdown?.bySeverity.NEEDS_IMPROVEMENT ?? 0,
             severity === 'NEEDS_IMPROVEMENT',
             () => { setSeverity('NEEDS_IMPROVEMENT'); setMembership(''); }, 'warning')}
        {kpi(t('status.NON_COMPLIANT'), breakdown?.bySeverity.NON_COMPLIANT ?? 0,
             severity === 'NON_COMPLIANT',
             () => { setSeverity('NON_COMPLIANT'); setMembership(''); }, 'critical')}
        {kpi(t('gaps.notInPlan'), breakdown?.notInPlan ?? 0, membership === 'NOT_IN_PLAN',
             () => { setMembership('NOT_IN_PLAN'); setSeverity(''); }, 'serious')}
      </div>

      {/* Filters */}
      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs"
            placeholder={t('gaps.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
          />
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            <select className="oc-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}
                    aria-label={t('gaps.breakdownStatus')}>
              <option value="">{t('common.all')}</option>
              <option value="OPEN">{t('status.OPEN')}</option>
              <option value="CLOSED">{t('status.CLOSED')}</option>
            </select>
            <select className="oc-input !w-auto" value={severity} onChange={(e) => setSeverity(e.target.value)}
                    aria-label={t('gaps.breakdownSeverity')}>
              <option value="">{t('common.all')}</option>
              <option value="NON_COMPLIANT">{t('status.NON_COMPLIANT')}</option>
              <option value="NEEDS_IMPROVEMENT">{t('status.NEEDS_IMPROVEMENT')}</option>
            </select>
            <select className="oc-input !w-auto" value={membership} onChange={(e) => setMembership(e.target.value)}
                    aria-label={t('gaps.breakdownPlan')}>
              <option value="">{t('common.all')}</option>
              <option value="IN_PLAN">{t('gaps.inPlan')}</option>
              <option value="NOT_IN_PLAN">{t('gaps.notInPlan')}</option>
            </select>
            <Button size="sm" onClick={() => { setSearch(''); setStatus(''); setSeverity(''); setMembership(''); }}>
              {t('common.clearFilters')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Grouped gap list */}
      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : listError ? (
        /* Distinct from the empty state on purpose. "No gaps found" when the
           request actually failed would read as "you are compliant", which is
           the most damaging thing this screen could get wrong. */
        <ErrorNotice
          message={describeError(listError)}
          onRetry={() => void refetch()}
          retryLabel={t('common.retry')}
        />
      ) : items.length === 0 ? (
        <Card><EmptyState icon={<IconGap className="w-10 h-10" />} title={t('gaps.none')} /></Card>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 px-1">
            {allows('plan.create')
              ? <p className="text-sm text-ink-muted">{t('gaps.hint')}</p>
              : <span />}
            <div
              className="flex items-center gap-0.5 shrink-0 rounded-lg border border-line p-0.5"
              role="group"
              aria-label={t('gaps.view', { defaultValue: 'View' })}
            >
              <ViewToggleButton active={view === 'tiles'} onClick={() => chooseView('tiles')}
                                icon={IconGrid} label={t('gaps.viewTiles', { defaultValue: 'Tiles' })} />
              <ViewToggleButton active={view === 'grouped'} onClick={() => chooseView('grouped')}
                                icon={IconList} label={t('gaps.viewList', { defaultValue: 'List' })} />
            </div>
          </div>

          {view === 'tiles' ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((gap) => (
                <GapTile
                  key={gap.id}
                  gap={gap}
                  selected={selected.has(gap.id)}
                  selectable={allows('plan.create') && gap.status === 'OPEN'}
                  onToggle={() => toggle(gap.id)}
                  reuse={reuseByGap.get(gap.id)}
                />
              ))}
            </div>
          ) : (
          <div className="space-y-3">
            {[...groups.entries()].map(([sectionKey, section]) => {
              const sectionCount = [...section.subgroups.values()]
                .reduce((sum, sub) => sum + sub.gaps.length, 0);
              const isCollapsed = collapsed.has(sectionKey);

              return (
                <Card key={sectionKey} className="!p-0 overflow-hidden">
                  <button
                    onClick={() => toggleGroup(sectionKey)}
                    aria-expanded={!isCollapsed}
                    className="w-full flex items-center justify-between gap-3 p-4 hover:bg-surface-sunken transition-colors text-left"
                  >
                    <span className="font-medium text-ink min-w-0">
                      {section.title}
                      <span className="ml-2 text-sm text-ink-muted font-normal">({sectionCount})</span>
                    </span>
                    <IconChevron
                      className={cn('w-4 h-4 text-ink-muted shrink-0 transition-transform',
                                    isCollapsed ? '' : 'rotate-90')}
                    />
                  </button>

                  {!isCollapsed && (
                    <div className="px-3 pb-3 space-y-3">
                      {[...section.subgroups.entries()].map(([subKey, sub]) => (
                        <div key={subKey} className="rounded-xl bg-surface-sunken p-3">
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-sm font-medium text-ink-secondary min-w-0">
                              {sub.title}
                            </span>
                            <span className="oc-pill-neutral shrink-0">
                              {t('gaps.groupGaps', { count: sub.gaps.length })}
                            </span>
                          </div>
                          <ul className="space-y-2">
                            {sub.gaps.map((gap) => (
                              <GapCard
                                key={gap.id}
                                gap={gap}
                                selected={selected.has(gap.id)}
                                selectable={allows('plan.create') && gap.status === 'OPEN'}
                                onToggle={() => toggle(gap.id)}
                                reuse={reuseByGap.get(gap.id)}
                              />
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
          )}
        </>
      )}

      {/* Selection bar, above the phone tab bar so it is always reachable. */}
      {selected.size > 0 && (
        <div className="fixed bottom-14 lg:bottom-6 inset-x-0 z-30 px-4
                        pb-[env(safe-area-inset-bottom)] animate-fade-in">
          <div className="mx-auto max-w-2xl rounded-2xl bg-surface-raised border border-line
                          shadow-raised p-3 flex items-center justify-between gap-3">
            <span className="text-sm text-ink font-medium">
              {t('gaps.selected', { count: selected.size })}
            </span>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setSelected(new Set())}>{t('gaps.clear')}</Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setError(null);
                  setPlanName(
                    selected.size === 1
                      ? `Plan: ${items.find((g) => selected.has(g.id))?.title.slice(0, 60) ?? ''}`
                      : `Remediation plan (${selected.size} gaps)`,
                  );
                  setPlanOpen(true);
                }}
              >
                {t('gaps.createPlan', { count: selected.size })}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title={t('plans.create')}
        footer={
          <>
            <Button onClick={() => setPlanOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={!planName.trim() || createPlan.isPending}
                    onClick={() => createPlan.mutate()}>
              {createPlan.isPending ? t('common.saving') : t('common.create')}
            </Button>
          </>
        }
      >
        {error && <div className="mb-4"><ErrorNotice message={error} /></div>}
        <Field label={t('plans.name')} htmlFor="plan-name" required>
          <input id="plan-name" className="oc-input" value={planName}
                 onChange={(e) => setPlanName(e.target.value)} maxLength={255} />
        </Field>
        <Field label={t('plans.dueDate')} htmlFor="plan-due" hint={t('common.optional')}>
          <input
            id="plan-due" type="date" className="oc-input" value={planDue}
            onChange={(e) => setPlanDue(e.target.value)}
            min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
          />
        </Field>
        <p className="oc-muted">{t('plans.linkedGaps')}: {selected.size}</p>
      </Modal>
    </div>
  );
}

/**
 * One gap.
 *
 * The left border carries severity as a second, non-textual cue — but every
 * status is also stated in words below, so colour never carries meaning alone.
 */
function GapCard({ gap, selected, selectable, onToggle, reuse }: {
  gap: Gap;
  selected: boolean;
  selectable: boolean;
  onToggle: () => void;
  /**
   * "The underlying control is already satisfied here." Undefined for the vast
   * majority of gaps, which is why it is passed in from a single list-wide
   * fetch rather than queried per card — fifty gaps would otherwise be fifty
   * requests.
   */
  reuse?: GapReuseEntry;
}) {
  const { t } = useTranslation();

  return (
    <li
      className={cn(
        'rounded-xl border bg-surface-raised p-3 border-l-4 transition-all',
        gap.severity === 'NON_COMPLIANT' ? 'border-l-critical' : 'border-l-warning',
        selected ? 'ring-2 ring-brand border-brand' : 'border-line',
      )}
    >
      <div className="flex items-start gap-3">
        {selectable && (
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[rgb(var(--brand))] shrink-0"
            checked={selected}
            onChange={onToggle}
            aria-label={gap.title}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink leading-snug">{gap.title}</p>

          {/* Provenance chips: where this finding came from. */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="oc-pill-neutral">{gap.framework.name}</span>
            {gap.categoryPath && (
              <span className="oc-pill-neutral">{gap.categoryPath.replace('>', '›')}</span>
            )}
            {gap.questionCode && (
              <span className="oc-pill-neutral font-mono">{gap.questionCode}</span>
            )}
          </div>

          {gap.answerLabel && (
            <p className="text-xs text-ink-secondary mt-2">
              <span className="font-medium">{t('gaps.answer')}:</span> {gap.answerLabel}
            </p>
          )}
          {gap.description && (
            <p className="text-xs text-ink-secondary mt-1 leading-relaxed">
              <span className="font-medium">{t('gaps.description')}:</span> {gap.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <Pill tone={TONE_FOR_STATUS[gap.severity] ?? 'neutral'}>
              {t(`status.${gap.severity}`)}
            </Pill>
            <Pill tone={gap.plans.length > 0 ? 'good' : 'neutral'}>
              {gap.plans.length > 0 ? t('gaps.inPlan') : t('gaps.notInPlan')}
            </Pill>
            {/* A gap with no evidence attached is materially weaker at audit.
                Surfaced here so it is visible without opening the record. */}
            {gap.evidenceMissing && (
              <Pill tone="serious">{t('gaps.evidenceMissing')}</Pill>
            )}
          </div>

          {/* "You may already have done this."
              Placed BELOW the severity pills rather than above them: the
              finding is still a finding, and a reuse hint rendered first would
              read as a dismissal of it. The note itself says in as many words
              that it does not close the gap. */}
          {reuse && <GapReuseNote entry={reuse} />}
        </div>
      </div>
    </li>
  );
}

/** The segmented control that switches between the tile grid and the grouped list. */
function ViewToggleButton({ active, onClick, icon: Icon, label }: {
  active: boolean;
  onClick: () => void;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={label}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm transition-colors',
        active ? 'bg-surface-sunken text-ink font-medium' : 'text-ink-secondary hover:text-ink',
      )}
    >
      <Icon className="w-4 h-4" aria-hidden />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/**
 * The icon that stands in for a gap's control category, so the grid reads at a
 * glance. Matched on the category path by keyword, with a neutral fallback —
 * colour still never carries meaning alone (the severity strip and the status
 * pills below say it in words).
 */
function categoryIcon(path: string | null): ComponentType<SVGProps<SVGSVGElement>> {
  const p = (path ?? '').toLowerCase();
  if (/encrypt|crypto|\bkey\b|certificate/.test(p)) return IconKey;
  if (/network|access|authentic|mfa|identity/.test(p)) return IconShield;
  if (/incident|detect|monitor|logging|event/.test(p)) return IconIncident;
  if (/supplier|vendor|third|supply|outsourc/.test(p)) return IconVendor;
  if (/people|aware|training|\bhr\b|personnel|human/.test(p)) return IconUsers;
  if (/develop|application|software|\bcode\b|vulnerab|patch/.test(p)) return IconScan;
  if (/backup|continuity|resilience|recovery|availability/.test(p)) return IconChecklist;
  if (/policy|governance|organis|organiz|management/.test(p)) return IconPolicy;
  return IconGap;
}

/**
 * One gap, as a tile.
 *
 * The same record as GapCard, laid out for the grid the product owner chose: a
 * severity strip across the top, a category glyph, the question, and the status
 * pills. The whole tile toggles selection so bulk plan creation works here too.
 */
function GapTile({ gap, selected, selectable, onToggle, reuse }: {
  gap: Gap;
  selected: boolean;
  selectable: boolean;
  onToggle: () => void;
  reuse?: GapReuseEntry;
}) {
  const { t } = useTranslation();
  const Icon = categoryIcon(gap.categoryPath);
  const critical = gap.severity === 'NON_COMPLIANT';

  return (
    <div
      onClick={selectable ? onToggle : undefined}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle();
              }
            }
          : undefined
      }
      aria-pressed={selectable ? selected : undefined}
      className={cn(
        'oc-card !p-0 overflow-hidden flex flex-col transition-all',
        selectable && 'cursor-pointer hover:border-brand/40',
        selected && 'ring-2 ring-brand border-brand',
      )}
    >
      <div className={cn('h-1 w-full', critical ? 'bg-critical' : 'bg-warning')} />
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'grid place-items-center w-9 h-9 rounded-[10px]',
              critical ? 'bg-critical/10 text-critical' : 'bg-warning/10 text-warning',
            )}
          >
            <Icon className="w-5 h-5" aria-hidden />
          </span>
          <div className="flex items-center gap-2">
            {gap.questionCode && (
              <span className="oc-pill-neutral font-mono">{gap.questionCode}</span>
            )}
            {selectable && (
              <input
                type="checkbox"
                className="w-4 h-4 accent-[rgb(var(--brand))] shrink-0"
                checked={selected}
                onChange={onToggle}
                onClick={(e) => e.stopPropagation()}
                aria-label={gap.title}
              />
            )}
          </div>
        </div>

        <p className="text-sm font-medium text-ink leading-snug text-balance">{gap.title}</p>

        <div className="flex flex-wrap gap-1">
          <span className="oc-pill-neutral">{gap.framework.name}</span>
          {gap.categoryPath && (
            <span className="oc-pill-neutral">{gap.categoryPath.replace('>', '›')}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-auto">
          <Pill tone={TONE_FOR_STATUS[gap.severity] ?? 'neutral'}>
            {t(`status.${gap.severity}`)}
          </Pill>
          <Pill tone={gap.plans.length > 0 ? 'good' : 'neutral'}>
            {gap.plans.length > 0 ? t('gaps.inPlan') : t('gaps.notInPlan')}
          </Pill>
          {gap.evidenceMissing && <Pill tone="serious">{t('gaps.evidenceMissing')}</Pill>}
        </div>

        {reuse && <GapReuseNote entry={reuse} />}
      </div>
    </div>
  );
}
